import { Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MbpButtonComponent, SearchComponent } from '@components/form';
import { LoadingMessageComponent, MbpNoContentMessageComponent } from '@components/messages';
import { ErrorHandlingService } from '@errors/error-handling.service';
import { createPageResource, filterBySearchTerm } from '@utils';
import { MbpModalComponent } from '../mbp-modal/mbp-modal.component';
import { SelectItem, SelectItemModalLabels } from './select-item.model';

@Component({
  selector: 'mbp-select-item-modal',
  templateUrl: './select-item-modal.component.html',
  styleUrl: './select-item-modal.component.scss',
  imports: [TranslateModule, MbpModalComponent, MbpButtonComponent, SearchComponent, LoadingMessageComponent, MbpNoContentMessageComponent]
})
export class SelectItemModalComponent {
  private readonly errorHandlingService = inject(ErrorHandlingService);

  loader = input.required<() => Promise<SelectItem[]>>();
  labels = input.required<SelectItemModalLabels>();
  action = input.required<(item: SelectItem) => Promise<void>>();
  initialSelectedId = input<SelectItem['id']>();

  close = output<void>();

  protected searchText = signal('');
  protected processing = signal(false);

  protected readonly itemsResource = createPageResource({
    loadAsync: (): Promise<SelectItem[]> => this.loader()(),
    errorMessage: () => this.labels().loadErrorMessage,
    fallbackValue: []
  });

  protected selectedItem = linkedSignal<SelectItem | undefined>(() => {
    const items = this.itemsResource.value();
    const initialId = this.initialSelectedId();
    if (!items || initialId === undefined) {
      return undefined;
    }
    return items.find((item) => item.id === initialId);
  });

  protected selectedItemId = computed(() => this.selectedItem()?.id);

  protected filteredItems = computed(() => filterBySearchTerm(this.itemsResource.value(), this.searchText(), (item) => item.text));

  protected onClose(): void {
    if (this.processing()) {
      return;
    }
    this.close.emit();
  }

  protected onSearchChange(searchText: string): void {
    this.searchText.set(searchText);
  }

  protected onSelectItem(item: SelectItem): void {
    this.selectedItem.set(item);
  }

  protected async onSave(): Promise<void> {
    const item = this.selectedItem();
    if (!item || this.processing()) {
      return;
    }

    this.processing.set(true);
    try {
      await this.action()(item);
      this.close.emit();
    } catch (error: unknown) {
      await this.errorHandlingService.handleErrorAsync(error, this.labels().saveErrorMessage);
    } finally {
      this.processing.set(false);
    }
  }
}

